<script setup lang="ts">
import { AppButton } from '../button';
import { PRINT_SIGNATURE } from './print';

/**
 * Каркас печатной формы: листы A4 с подписью mors.pro внизу. `copies` экземпляров идут
 * с разрывом страницы, слот `after` печатается следом со своей разметкой страницы.
 * Слот `blocked` (например, не хватает реквизитов) заменяет листы, кнопки «Печать» нет.
 */
withDefaults(defineProps<{ orientation?: 'portrait' | 'landscape'; copies?: number }>(), {
  orientation: 'portrait',
  copies: 1,
});
defineSlots<{ default?: (props: { copy: number }) => unknown; blocked?: () => unknown; after?: () => unknown }>();

const print = (): void => window.print();
const close = (): void => window.close();
</script>

<template>
  <div class="min-h-screen bg-(--base-main-bg) py-6 print:bg-white print:py-0">
    <div
      class="mx-auto mb-4 flex max-w-full justify-end gap-2 print:hidden"
      :class="orientation === 'landscape' ? 'w-[297mm]' : 'w-[210mm]'"
    >
      <AppButton
        v-if="!$slots.blocked"
        type="button"
        @click="print"
      >
        Печать
      </AppButton>
      <AppButton
        type="button"
        outline
        @click="close"
      >
        Закрыть
      </AppButton>
    </div>
    <div
      v-if="$slots.blocked"
      class="mx-auto w-[210mm] max-w-full rounded-xl bg-(--base-container-bg) p-6 text-sm"
    >
      <slot name="blocked" />
    </div>
    <div
      v-else
      class="space-y-6 print:space-y-0"
    >
      <article
        v-for="copy in copies"
        :key="copy"
        class="mx-auto flex max-w-full break-before-page flex-col gap-5 bg-white leading-snug text-black shadow first:break-before-auto print:w-auto print:p-0 print:shadow-none"
        :class="
          orientation === 'landscape'
            ? 'print-landscape-sheet min-h-[210mm] w-[297mm] p-[8mm] text-[8pt] print:min-h-[194mm]'
            : 'min-h-[297mm] w-[210mm] p-[12mm] text-[11pt] print:min-h-[272mm]'
        "
      >
        <slot :copy="copy - 1" />
        <footer class="mt-auto pt-4 text-center text-[8pt] text-black/60">{{ PRINT_SIGNATURE }}</footer>
      </article>
      <slot name="after" />
    </div>
  </div>
</template>

<style>
@page {
  size: A4;
  margin: 12mm;
}

@page landscape {
  size: A4 landscape;
  margin: 8mm;
}

.print-landscape-sheet {
  page: landscape;
}
</style>
